/**
 * MilestoneWidget
 *
 * Pattern: DetailPanel (pannello laterale destro, mobile = full screen)
 * Unified with all other widgets in the platform for consistent 50% width layout.
 */

import { useState, useMemo } from 'react';
import { Flag, Search, User, CheckCircle2, Link, X } from 'lucide-react';
import { useProjectsStore } from '@business-assistant/shared';
import { DetailPanel, DetailSection, DetailActions } from '../common/base';
import { useTranslation } from '../../hooks/useTranslation';
import { Button, TextField, TextAreaField } from '../common/forms';

interface MilestoneFormProps {
  projectId: string;
  milestoneId?: string; // If provided, edit mode
  onClose: () => void;
  onSave: (data: MilestoneFormData) => void;
}

export interface MilestoneFormData {
  title: string;
  description?: string;
  dueDate: string;
  completed: boolean;
  completedAt?: string;
  involvedUsers?: string[];
  linkedTaskIds?: string[];
}

export function MilestoneWidget({ projectId, milestoneId, onClose, onSave }: MilestoneFormProps) {
  const { t } = useTranslation();
  const { getProjectMilestones, getProjectTasks, getProject } = useProjectsStore();

  const milestones = getProjectMilestones(projectId);
  const tasks = getProjectTasks(projectId);
  const project = getProject(projectId);
  const existingMilestone = milestoneId ? milestones.find(m => m.id === milestoneId) : null;

  const [formData, setFormData] = useState<MilestoneFormData>({
    title: existingMilestone?.title || '',
    description: existingMilestone?.description || '',
    dueDate: existingMilestone?.dueDate?.split('T')[0] || '',
    completed: existingMilestone?.completed || false,
    completedAt: existingMilestone?.completedAt,
    involvedUsers: existingMilestone?.involvedUsers || [],
    linkedTaskIds: existingMilestone?.linkedTaskIds || [],
  });

  const [showTaskSelector, setShowTaskSelector] = useState(false);
  const [taskSearch, setTaskSearch] = useState('');

  // Filter tasks based on search
  const filteredTasks = useMemo(() => {
    const searchLower = taskSearch.toLowerCase();
    return tasks.filter(task =>
      task.title.toLowerCase().includes(searchLower) &&
      !formData.linkedTaskIds?.includes(task.id)
    );
  }, [tasks, taskSearch, formData.linkedTaskIds]);

  // Get linked tasks details
  const linkedTasks = useMemo(() => {
    return tasks.filter(t => formData.linkedTaskIds?.includes(t.id));
  }, [tasks, formData.linkedTaskIds]);

  // Team members from project
  const teamMembers = useMemo(() => {
    return project?.team || [];
  }, [project]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      ...formData,
      completedAt: formData.completed ? (formData.completedAt || new Date().toISOString()) : undefined,
    });
  };

  const handleLinkTask = (taskId: string) => {
    setFormData({
      ...formData,
      linkedTaskIds: [...(formData.linkedTaskIds || []), taskId],
    });
    setTaskSearch('');
    setShowTaskSelector(false);
  };

  const handleUnlinkTask = (taskId: string) => {
    setFormData({
      ...formData,
      linkedTaskIds: formData.linkedTaskIds?.filter(id => id !== taskId) || [],
    });
  };

  const handleToggleUser = (userId: string) => {
    const current = formData.involvedUsers || [];
    if (current.includes(userId)) {
      setFormData({ ...formData, involvedUsers: current.filter(id => id !== userId) });
    } else {
      setFormData({ ...formData, involvedUsers: [...current, userId] });
    }
  };

  return (
    <DetailPanel
      title={milestoneId ? t('milestones.editMilestone') : t('milestones.newMilestone')}
      icon={<Flag size={20} style={{ color: '#ec4899' }} />}
      onClose={onClose}
      accentColor="#ec4899"
      width="50%"
    >
      <form onSubmit={handleSubmit}>
        {/* Title */}
        <DetailSection title={t('milestones.titleRequired')}>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            required
            placeholder={t('milestones.titlePlaceholder')}
            style={{
              width: '100%', padding: '0.625rem', border: '1px solid var(--line)',
              borderRadius: '0.375rem', fontSize: '0.875rem',
            }}
          />
        </DetailSection>

        {/* Description */}
        <DetailSection title={t('milestones.description')}>
          <textarea
            value={formData.description || ''}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder={t('milestones.descriptionPlaceholder')}
            rows={3}
            style={{
              width: '100%', padding: '0.625rem', border: '1px solid var(--line)',
              borderRadius: '0.375rem', fontSize: '0.875rem', resize: 'vertical',
            }}
          />
        </DetailSection>

        {/* Due Date & Status Row */}
        <DetailSection title={t('milestones.dueDateAndStatus')}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            {/* Due Date */}
            <div>
              <label style={{ display: 'block', fontWeight: 500, marginBottom: '0.375rem', fontSize: '0.875rem' }}>
                {t('milestones.dueDateRequired')}
              </label>
              <input
                type="date"
                value={formData.dueDate}
                onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                required
                style={{
                  width: '100%', padding: '0.625rem', border: '1px solid var(--line)',
                  borderRadius: '0.375rem', fontSize: '0.875rem',
                }}
              />
            </div>

            {/* Completed */}
            <div>
              <label style={{ display: 'block', fontWeight: 500, marginBottom: '0.375rem', fontSize: '0.875rem' }}>
                {t('milestones.statusLabel')}
              </label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <label style={{
                  flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  gap: '0.375rem', padding: '0.625rem',
                  border: `2px solid ${!formData.completed ? '#3b82f6' : 'var(--line)'}`,
                  borderRadius: '0.375rem', cursor: 'pointer',
                  background: !formData.completed ? '#dbeafe' : 'white',
                  fontSize: '0.8125rem', fontWeight: 500,
                }}>
                  <input
                    type="radio"
                    name="completed"
                    checked={!formData.completed}
                    onChange={() => setFormData({ ...formData, completed: false, completedAt: undefined })}
                    style={{ display: 'none' }}
                  />
                  {t('milestones.inProgress')}
                </label>
                <label style={{
                  flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  gap: '0.375rem', padding: '0.625rem',
                  border: `2px solid ${formData.completed ? '#22c55e' : 'var(--line)'}`,
                  borderRadius: '0.375rem', cursor: 'pointer',
                  background: formData.completed ? '#dcfce7' : 'white',
                  fontSize: '0.8125rem', fontWeight: 500,
                }}>
                  <input
                    type="radio"
                    name="completed"
                    checked={formData.completed}
                    onChange={() => setFormData({ ...formData, completed: true, completedAt: new Date().toISOString() })}
                    style={{ display: 'none' }}
                  />
                  <CheckCircle2 size={14} />
                  {t('milestones.completed')}
                </label>
              </div>
            </div>
          </div>
        </DetailSection>

        {/* Linked Tasks */}
        <DetailSection title={<><Link size={14} style={{ marginRight: '0.375rem', verticalAlign: 'middle' }} />{t('milestones.linkedTasks')}</>}>
          {/* Linked tasks list */}
          {linkedTasks.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem', marginBottom: '0.5rem' }}>
              {linkedTasks.map((task) => (
                <div
                  key={task.id}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '0.5rem',
                    padding: '0.5rem 0.75rem', background: 'var(--bg)', borderRadius: '0.375rem',
                  }}
                >
                  <CheckCircle2
                    size={14}
                    style={{ color: task.status === 'done' ? '#22c55e' : 'var(--text-muted)' }}
                  />
                  <span style={{
                    flex: 1, fontSize: '0.875rem',
                    textDecoration: task.status === 'done' ? 'line-through' : 'none',
                    color: task.status === 'done' ? 'var(--text-muted)' : 'var(--text)',
                  }}>
                    {task.title}
                  </span>
                  <button
                    type="button"
                    onClick={() => handleUnlinkTask(task.id)}
                    style={{
                      background: 'none', border: 'none', padding: '0.25rem',
                      cursor: 'pointer', color: 'var(--text-muted)',
                      fontSize: '0.75rem',
                    }}
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Task search */}
          <div style={{ position: 'relative' }}>
            <Search
              size={16}
              style={{
                position: 'absolute', left: '0.625rem', top: '50%', transform: 'translateY(-50%)',
                color: 'var(--text-muted)', pointerEvents: 'none',
              }}
            />
            <input
              type="text"
              value={taskSearch}
              onChange={(e) => { setTaskSearch(e.target.value); setShowTaskSelector(true); }}
              onFocus={() => setShowTaskSelector(true)}
              onBlur={() => setTimeout(() => setShowTaskSelector(false), 200)}
              placeholder={t('milestones.searchTasksToLink')}
              style={{
                width: '100%', padding: '0.625rem', paddingLeft: '2.25rem',
                border: '1px solid var(--line)', borderRadius: '0.375rem', fontSize: '0.875rem',
              }}
            />
            {/* Task dropdown */}
            {showTaskSelector && filteredTasks.length > 0 && (
              <div style={{
                position: 'absolute', top: '100%', left: 0, right: 0,
                background: 'white', border: '1px solid var(--line)', borderRadius: '0.375rem',
                boxShadow: 'var(--shadow-hover)', zIndex: 10, maxHeight: '200px', overflowY: 'auto',
                marginTop: '0.25rem',
              }}>
                {filteredTasks.map((task) => (
                  <button
                    key={task.id}
                    type="button"
                    onClick={() => handleLinkTask(task.id)}
                    style={{
                      width: '100%', padding: '0.625rem', background: 'transparent',
                      border: 'none', borderBottom: '1px solid var(--line)',
                      display: 'flex', alignItems: 'center', gap: '0.5rem',
                      cursor: 'pointer', textAlign: 'left',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    <CheckCircle2
                      size={14}
                      style={{ color: task.status === 'done' ? '#22c55e' : 'var(--text-muted)' }}
                    />
                    <span style={{ flex: 1, fontSize: '0.875rem' }}>{task.title}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </DetailSection>

        {/* Involved Users */}
        {teamMembers.length > 0 && (
          <DetailSection title={<><User size={14} style={{ marginRight: '0.375rem', verticalAlign: 'middle' }} />{t('milestones.involvedPeople')}</>}>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              {teamMembers.map((member) => {
                const isSelected = formData.involvedUsers?.includes(member.userId);
                return (
                  <button
                    key={member.userId}
                    type="button"
                    onClick={() => handleToggleUser(member.userId)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '0.375rem',
                      padding: '0.375rem 0.75rem',
                      border: `2px solid ${isSelected ? 'var(--brand)' : 'var(--line)'}`,
                      borderRadius: '9999px', cursor: 'pointer',
                      background: isSelected ? 'var(--brand-soft)' : 'white',
                      fontSize: '0.8125rem',
                    }}
                  >
                    <div style={{
                      width: '20px', height: '20px', borderRadius: '50%',
                      background: 'var(--brand)', color: 'white',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '0.625rem', fontWeight: 600,
                    }}>
                      {member.displayName?.[0] || '?'}
                    </div>
                    {member.displayName}
                  </button>
                );
              })}
            </div>
          </DetailSection>
        )}

        {/* Actions */}
        <DetailActions>
          <Button variant="secondary" type="button" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" type="submit">
            {milestoneId ? t('milestones.saveChanges') : t('milestones.createMilestone')}
          </Button>
        </DetailActions>
      </form>
    </DetailPanel>
  );
}
